import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { StorageProvider } from './services/storageContext';
import AuthProvider from './components/AuthProvider';
import ProtectedRoute, { AdminRoute, OperationsRoute, ProjectAdminRoute } from './components/ProtectedRoute';
import LoadingScreen from './components/LoadingScreen';
import UnprofiledScreen from './components/UnprofiledScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { usePermissions } from './hooks/usePermissions';
import useSuppressResizeObserverErrors from './hooks/useSuppressResizeObserverErrors';
import VersionCheck from './components/VersionCheck';
import { ThemeProvider } from './services/themeContext';
import { ToastProvider } from './services/toastContext';
import ToastContainer from './components/ToastContainer';
import { isAgentMapV2Enabled } from './components/agentmap/mapFlags';
import MobileChatPage from './components/mobile/MobileChatPage';

// Lazy load components for better performance
const ProjectManagementPage = lazy(() => import('./components/ProjectManagementPage'));
const ProjectDetailPage = lazy(() => import('./components/ProjectDetailPage'));
const AgentMapPage = lazy(() => import('./components/AgentMapPage'));
const AgentMapPageV2 = lazy(() => import('./components/agentmap/AgentMapPageV2'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const StreamingChatPage = lazy(() => import('./components/StreamingChatPage'));
const MCPServersPage = lazy(() => import('./components/MCPServersPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const DashboardBuilderPage = lazy(() => import('./components/dashboard/DashboardBuilderPage'));
const DashboardKioskPage = lazy(() => import('./components/dashboard/DashboardKioskPage'));
const AgentLibrary = lazy(() => import('./pages/AgentLibrary'));
const SkillLibrary = lazy(() => import('./pages/SkillLibrary'));
const TracesPage = lazy(() => import('./components/TracesPage'));
const TraceDetailPage = lazy(() => import('./components/TraceDetailPage'));
const ProjectTracesPage = lazy(() => import('./components/ProjectTracesPage'));
const CliAuthPage = lazy(() => import('./components/CliAuthPage'));

/**
 * App routing with role-based navigation
 */
const AppRoutes = () => {
  const { getDefaultRoute } = usePermissions();
  
  return (
    <Suspense fallback={<LoadingScreen message="Loading application..." />}>
      <Routes>
        {/* Auth callback route */}
        <Route path="/auth/callback" element={<Navigate to={getDefaultRoute()} replace />} />
        
        {/* react-oidc-context's <AuthProvider> (src/components/AuthProvider.js) detects
            the code/state query params on whatever path the redirect lands on and
            resolves the sign-in there — no dedicated route is needed for it. */}
        
        {/* Unprofiled users screen */}
        <Route path="/awaiting-approval" element={<UnprofiledScreen />} />
        
        {/* Error page */}
        <Route path="/error" element={
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="glass-panel glass-panel--tight max-w-lg w-full text-center">
              <div className="glass-panel__content">
                <div className="text-danger text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-semibold text-glass-primary mb-3">
                  Something went wrong
                </h2>
                <p className="text-glass-secondary mb-6">
                  We encountered an issue while loading the application. You can retry or head back to the home surface.
                </p>
                <Link to="/" className="btn btn-primary">
                  Return Home
                </Link>
              </div>
            </div>
          </div>
        } />
        
        {/* Admin hub - platform admins (all tabs) plus project/customer admins (operations, read-only) */}
        <Route
          path="/admin"
          element={
            <OperationsRoute>
              <AdminPage />
            </OperationsRoute>
          }
        />
        
        {/* Settings route - project admin and above */}
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Protected application routes */}
        <Route 
          path="/config" 
          element={
            <ProtectedRoute requiredPermission="project_settings">
              <ProjectManagementPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/mcp-servers"
          element={
            <AdminRoute>
              <MCPServersPage />
            </AdminRoute>
          }
        />

        {/* Traces routes */}
        <Route
          path="/traces"
          element={
            <AdminRoute>
              <TracesPage />
            </AdminRoute>
          }
        />

        <Route
          path="/traces/:traceId"
          element={
            <ProjectAdminRoute>
              <TraceDetailPage />
            </ProjectAdminRoute>
          }
        />

        {/* Project-scoped traces - accessible to project admins */}
        <Route
          path="/projects/:projectId/traces"
          element={
            <ProjectAdminRoute>
              <ProjectTracesPage />
            </ProjectAdminRoute>
          }
        />

        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          } 
        />

        <Route
          path="/projects/:projectId/dashboard"
          element={
            <ProtectedRoute>
              <DashboardBuilderPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:projectId/dashboards/:dashboardId/kiosk"
          element={
            <ProtectedRoute>
              <DashboardKioskPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/projects/:projectId/agent-map"
          element={
            <ProjectAdminRoute>
              {isAgentMapV2Enabled() ? <AgentMapPageV2 /> : <AgentMapPage />}
            </ProjectAdminRoute>
          }
        />
        
        {/* Agent Library route */}
        <Route 
          path="/library" 
          element={
            <ProtectedRoute>
              <AgentLibrary />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/library/agents/:agentId"
          element={
            <ProtectedRoute>
              <AgentLibrary />
            </ProtectedRoute>
          }
        />

        {/* Skill Library route — browse/deploy is authenticated; the deploy
            call itself is authorized against the chosen target project. */}
        <Route
          path="/skill-library"
          element={
            <ProtectedRoute>
              <SkillLibrary />
            </ProtectedRoute>
          }
        />

        {/* Chat routes */}
        <Route 
          path="/chat" 
          element={
            <ProtectedRoute>
              <LandingPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/projects/:projectId/chat/:agentId" 
          element={
            <ProtectedRoute>
              <StreamingChatPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/streamingchat/:projectId" 
          element={
            <ProtectedRoute>
              <StreamingChatPage />
            </ProtectedRoute>
          } 
        />
        
        {/* CLI authorization approval route */}
        <Route
          path="/cli-auth"
          element={
            <ProtectedRoute>
              <CliAuthPage />
            </ProtectedRoute>
          }
        />

        {/* Default route - redirect to user's default route */}
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  const location = useLocation();
  useSuppressResizeObserverErrors();

  // Mobile routes bypass the SPA's OIDC session entirely — SAP cookie auth handled by backend.
  // Uses includes() to support SAP Web Dispatcher proxy prefixes
  // (e.g. /tctm_ewm/ai/m/chat → pathname contains /m/).
  if (location.pathname.includes('/m/')) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<LoadingScreen message="Loading..." />}>
            <Routes>
              <Route path="*" element={<MobileChatPage />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Existing app shell, authenticated via Keycloak/OIDC (AuthProvider below)
  const immersiveShell = /\/streamingchat|\/projects\/.+\/chat|\/projects\/.+\/traces|\/chat|\/admin|\/settings|\/config|\/traces/.test(location.pathname);
  const isTraceDetail = /\/traces\/[^/]+$/.test(location.pathname);
  const shellClassName = immersiveShell ? 'app-shell app-shell--immersive' : 'app-shell';
  const viewportClassName = `app-shell__viewport${isTraceDetail ? '' : ' glass-theme'}`;

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <StorageProvider>
          <AuthProvider>
            <ToastProvider>
              <VersionCheck />
              <ToastContainer />
              <div className={shellClassName}>
                <div className="app-shell__background" aria-hidden="true" />
                <main className={viewportClassName}>
                  <AppRoutes />
                </main>
              </div>
            </ToastProvider>
          </AuthProvider>
        </StorageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 
